import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { useEffect } from "react";
import { GoogleOAuthProvider } from '@react-oauth/google';

// Auth Context
import { AuthProvider } from "./contexts/AuthContext";

// Auth Callback for Emergent Auth
import AuthCallback from "./components/AuthCallback";

// Google Client ID
const GOOGLE_CLIENT_ID = '979995496170-2fk0nlgticuejpsblek8g9nldvm0ehgo.apps.googleusercontent.com';

// Components - v4.5 structure
import Navbar from "./components/Navbar";
import Hero from "./components/Hero";
import GradientFadeLine from "./components/GradientFadeLine";
import CompatibilityMarquee from "./components/TrustBar";
import HowItWorks from "./components/HowItWorks";
import AgentTypes from "./components/AgentTypes";
import CTAFooter from "./components/CTAFooter";

// Pages
import EnaiblPage from "./pages/EnaiblPage";
import PluginsPageMarketing from "./pages/PluginsPage";
import ChainsPage from "./pages/ChainsPage";
import TermsPage from "./pages/TermsPage";
import PrivacyPage from "./pages/PrivacyPage";
import LoginPage from "./pages/LoginPage";

// Documentation Pages
import DocsIntroduction from "./pages/docs/Introduction";
import DocsQuickStart from "./pages/docs/QuickStart";
import DocsArchitecture from "./pages/docs/Architecture";
import DocsAgents from "./pages/docs/Agents";
import DocsPlugins from "./pages/docs/Plugins";
import DocsWallets from "./pages/docs/Wallets";
import DocsMultiSig from "./pages/docs/MultiSig";
import DocsTokenomics from "./pages/docs/Tokenomics";
import DocsGovernance from "./pages/docs/Governance";
import DocsFees from "./pages/docs/Fees";
import DocsApiAuth from "./pages/docs/api/Auth";
import DocsApiEndpoints from "./pages/docs/api/Endpoints";
import DocsApiWebhooks from "./pages/docs/api/Webhooks";

// Dashboard components
import DashboardLayout from "./components/dashboard/DashboardLayout";
import AgentsPage from "./components/dashboard/AgentsPage";
import AgentDetailPage from "./components/dashboard/AgentDetailPage";
import WalletsPage from "./components/dashboard/WalletsPage";
import PluginsPage from "./components/dashboard/PluginsPage";
import TransactionsPage from "./components/dashboard/TransactionsPage";
import EnaiblDashboardPage from "./components/dashboard/EnaiblPage";
import SettingsPage from "./components/dashboard/SettingsPage";
import LeaderboardPage from "./components/dashboard/LeaderboardPage";
import AccountSettingsPage from "./components/dashboard/AccountSettingsPage";
import MultiSigPage from "./components/dashboard/MultiSigPage";
import SafeSignPage from "./components/dashboard/SafeSignPage";

// Scroll to top on route change
function ScrollToTop() {
  const { pathname } = useLocation();
  
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [pathname]);
  
  return null;
}

// Remove any injected badges
const useRemoveBadges = () => {
  useEffect(() => {
    const removeBadge = () => {
      // Remove any elements with emergent in class/id
      document.querySelectorAll('[class*="emergent"], [id*="emergent"], a[href*="emergent.sh"]').forEach(el => {
        if (el && el.parentNode) {
          el.parentNode.removeChild(el);
        }
      });
      // Remove fixed positioned elements at bottom-right that aren't part of our app
      document.querySelectorAll('body > div').forEach(el => {
        if (el.id !== 'root' && !el.classList.contains('App')) {
          const style = window.getComputedStyle(el);
          if (style.position === 'fixed' && style.bottom !== 'auto' && style.right !== 'auto') {
            el.remove();
          }
        }
      });
    };
    
    // Run immediately and after delays to catch dynamically injected elements
    removeBadge();
    const t1 = setTimeout(removeBadge, 100);
    const t2 = setTimeout(removeBadge, 500);
    const t3 = setTimeout(removeBadge, 1000);
    const t4 = setTimeout(removeBadge, 2000);
    
    // Also observe for new elements
    const observer = new MutationObserver(removeBadge);
    observer.observe(document.body, { childList: true, subtree: true });
    
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
      clearTimeout(t4);
      observer.disconnect();
    };
  }, []);
};

const Home = () => {
  useRemoveBadges();
  return (
    <div className="min-h-screen bg-black text-white">
      <Navbar />
      <main>
        <Hero />
        <GradientFadeLine />
        <CompatibilityMarquee />
        <GradientFadeLine />
        <HowItWorks />
        <GradientFadeLine />
        <AgentTypes />
        <GradientFadeLine />
        <CTAFooter />
      </main>
    </div>
  );
};

function App() {
  return (
    <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
      <div className="App">
        <AuthProvider>
          <BrowserRouter>
            <AppRouter />
          </BrowserRouter>
        </AuthProvider>
      </div>
    </GoogleOAuthProvider>
  );
}

// CRITICAL: Check for session_id in URL fragment BEFORE rendering normal routes
// This prevents race conditions with auth state checking
function AppRouter() {
  const location = useLocation();
  
  // Check URL fragment for session_id (Emergent Auth callback)
  // MUST use useLocation().hash, NOT window.location.hash (not reactive)
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  
  return (
    <>
      <ScrollToTop />
      <Routes>
        {/* Marketing Pages */}
        <Route path="/" element={<Home />} />
        <Route path="/enaibl" element={<EnaiblPage />} />
        <Route path="/plugins" element={<PluginsPageMarketing />} />
        <Route path="/chains" element={<ChainsPage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/login" element={<LoginPage />} />
        
        {/* Documentation Routes */}
        <Route path="/docs" element={<DocsIntroduction />} />
        <Route path="/docs/quickstart" element={<DocsQuickStart />} />
        <Route path="/docs/architecture" element={<DocsArchitecture />} />
        <Route path="/docs/agents" element={<DocsAgents />} />
        <Route path="/docs/plugins" element={<DocsPlugins />} />
        <Route path="/docs/wallets" element={<DocsWallets />} />
        <Route path="/docs/multisig" element={<DocsMultiSig />} />
        <Route path="/docs/tokenomics" element={<DocsTokenomics />} />
        <Route path="/docs/governance" element={<DocsGovernance />} />
        <Route path="/docs/fees" element={<DocsFees />} />
        <Route path="/docs/api/auth" element={<DocsApiAuth />} />
        <Route path="/docs/api/endpoints" element={<DocsApiEndpoints />} />
        <Route path="/docs/api/webhooks" element={<DocsApiWebhooks />} />
        
        {/* Dashboard Routes */}
        <Route path="/dashboard" element={<DashboardLayout><AgentsPage /></DashboardLayout>} />
        <Route path="/dashboard/agents" element={<DashboardLayout><AgentsPage /></DashboardLayout>} />
        <Route path="/dashboard/agents/:agentId" element={<DashboardLayout><AgentDetailPage /></DashboardLayout>} />
        <Route path="/dashboard/wallets" element={<DashboardLayout><WalletsPage /></DashboardLayout>} />
        <Route path="/dashboard/plugins" element={<DashboardLayout><PluginsPage /></DashboardLayout>} />
        <Route path="/dashboard/multisig" element={<DashboardLayout><MultiSigPage /></DashboardLayout>} />
        <Route path="/dashboard/safesign" element={<DashboardLayout><SafeSignPage /></DashboardLayout>} />
        <Route path="/dashboard/transactions" element={<DashboardLayout><TransactionsPage /></DashboardLayout>} />
        <Route path="/dashboard/leaderboard" element={<DashboardLayout><LeaderboardPage /></DashboardLayout>} />
        <Route path="/dashboard/enaibl" element={<DashboardLayout><EnaiblDashboardPage /></DashboardLayout>} />
        <Route path="/dashboard/settings" element={<DashboardLayout><SettingsPage /></DashboardLayout>} />
        <Route path="/dashboard/account" element={<DashboardLayout><AccountSettingsPage /></DashboardLayout>} />
      </Routes>
    </>
  );
}

export default App;
